import { Box, TextField } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import PropTypes from "prop-types";
import { forwardRef, useState } from "react";
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import AsyncCreatableSelect from 'react-select/async-creatable';
import CreatableSelect from 'react-select/creatable';

const HiddenInput = forwardRef((props, ref) => (
    <div ref={ref} style={{ display: 'none' }} {...props} />
));

const CustomReactSelect = forwardRef(
    (
        {
            error,
            success,
            disabled,
            label,
            fullWidth = true,
            required = false,
            async = false,
            creatable = false,
            customStyles = {},
            value,
            placeholder,
            name, // <-- add name prop for form integration
            isMulti = false,
            options = [],
            onChange,
            helperText,
            ...rest
        },
        ref
    ) => {
        const theme = useTheme();
        const [isFocused, setIsFocused] = useState(false);
        const [menuIsOpen, setMenuIsOpen] = useState(false);


        const hasValue = value !== null && value !== undefined && value !== "" &&
            (Array.isArray(value) ? value.length > 0 : true);
        const shouldShrink = isFocused || hasValue || menuIsOpen;

        // Simple required validation error (always validate immediately if required)
        const [wasTouched, setWasTouched] = useState(false);
        const isError = error || (required && wasTouched && (!value || (Array.isArray(value) && value.length === 0)));

        // Blur handled inline; touch state updated there

        // Create Material-UI compatible styles for react-select
        const reactSelectStyles = {
            control: (provided, state) => ({
                ...provided,
                minHeight: '44px',
                // Allow auto height for multi-value wrapping
                ...(isMulti ? { height: 'auto' } : {}),
                border: isMulti ? provided.border : disabled ? '1px solid ' + theme.palette.grey[300] : 'none',
                borderRadius: '4px',
                backgroundColor: isMulti ? provided.backgroundColor : disabled ? '#f2f3f2' : 'transparent',
                boxShadow: isMulti ? provided.boxShadow : 'none',
                cursor: isMulti ? provided.cursor : disabled ? 'default' : 'pointer',
                '&:hover': {
                    border: isMulti ? provided.border : 'none',
                },
                fontSize: '0.875rem',
                fontFamily: theme.typography.fontFamily,
                paddingLeft: '14px',
                paddingRight: '2px',
                ...customStyles.control?.(provided, state),
            }),
            valueContainer: (provided) => ({
                ...provided,
                padding: isMulti ? '9px 0 2px' : shouldShrink ? '5px 0 2px' : '10px 0',
                margin: 0,
                flexWrap: isMulti ? 'wrap' : 'nowrap',
                ...(isMulti ? { rowGap: '4px' } : {}),
                ...customStyles.valueContainer?.(provided),
            }),
            input: (provided) => ({
                ...provided,
                margin: 0,
                padding: 0,
                fontSize: '0.875rem',
                ...customStyles.input?.(provided),
            }),
            placeholder: (provided) => ({
                ...provided,
                fontSize: '0.875rem',
                margin: 0,
                opacity: shouldShrink ? 1 : 0,
                ...customStyles.placeholder?.(provided),
            }),
            singleValue: (provided) => ({
                ...provided,
                color: theme.palette.text.primary,
                fontSize: '0.875rem',
                margin: 0,
                ...customStyles.singleValue?.(provided),
            }),
            multiValue: (provided) => ({
                ...provided,
                ...customStyles.multiValue?.(provided),
            }),
            multiValueLabel: (provided) => ({
                ...provided,
                ...customStyles.multiValueLabel?.(provided),
            }),
            multiValueRemove: (provided) => ({
                ...provided,
                '&:hover': {
                    backgroundColor: theme.palette.error.main,
                    color: theme.palette.error.contrastText,
                },
                ...customStyles.multiValueRemove?.(provided),
            }),
            menu: (provided) => ({
                ...provided,
                borderRadius: theme.shape.borderRadius,
                boxShadow: theme.shadows[8],
                border: 'none',
                zIndex: 9999,
                fontSize: '0.875rem',
                ...customStyles.menu?.(provided),
            }),
            menuList: (provided) => ({
                ...provided,
                padding: '8px 0',
                ...customStyles.menuList?.(provided),
            }),
            option: (provided, state) => ({
                ...provided,
                fontSize: '0.875rem',
                padding: '8px 10px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                '&:hover': {
                    backgroundColor: state.isSelected
                        ? theme.palette.primary.info
                        : theme.palette.action.hover,
                },
                // Add checkbox styles for multi-select
                ...(isMulti && {
                    '&::before': {
                        content: '""',
                        display: 'inline-block',
                        width: '16px',
                        height: '16px',
                        marginRight: '8px',
                        border: `2px solid ${state.isSelected ? theme.palette.info.main : theme.palette.grey[400]}`,
                        borderRadius: '3px',
                        backgroundColor: state.isSelected ? theme.palette.info.main : 'transparent',
                        backgroundImage: state.isSelected ? `url("data:image/svg+xml,%3csvg viewBox='0 0 16 16' fill='white' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='m13.854 3.646-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 9.293l6.646-6.647a.5.5 0 0 1 .708.708z'/%3e%3c/svg%3e")` : 'none',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center',
                        backgroundSize: '12px',
                    }
                }),
                ...customStyles.option?.(provided, state),
            }),
            loadingIndicator: (provided) => ({
                ...provided,
                color: theme.palette.primary.info,
                ...customStyles.loadingIndicator?.(provided),
            }),
        };

        // Choose the appropriate select component based on async and creatable props
        let SelectComponent;
        if (async && creatable) {
            SelectComponent = AsyncCreatableSelect;
        } else if (async) {
            SelectComponent = AsyncSelect;
        } else if (creatable) {
            SelectComponent = CreatableSelect;
        } else {
            SelectComponent = Select;
        }

        // Add a hidden input for native form validation
        // This will trigger browser's required prompt on submit
        const hiddenInputValue = Array.isArray(value)
            ? value.map(v => v.value).join(",")
            : value?.value || "";


        // For multi-select, render without the MUI TextField overlay to allow natural height growth
        if (isMulti) {
            return (
                <Box sx={{ width: fullWidth ? '100%' : 'auto', position: 'relative' }}>
                    {/* Floating label for multi-select */}
                    <Box
                        component="label"
                        sx={{
                            position: 'absolute',
                            top: shouldShrink ? '-8px' : '10px',
                            left: '14px',
                            fontSize: shouldShrink ? '0.75rem' : '0.875rem',
                            color: isError
                                ? theme.palette.error.main
                                : isFocused || menuIsOpen
                                    ? theme.palette.info.main
                                    : theme.palette.text.secondary,
                            backgroundColor: disabled ? 'transparent' : '#fff',
                            padding: shouldShrink ? '0 4px' : '0',
                            zIndex: 1,
                            transform: shouldShrink ? 'translateY(0)' : 'translateY(0)',
                            transition: 'all 0.2s ease-in-out',
                            pointerEvents: 'none',
                            fontFamily: theme.typography.fontFamily,
                            ...(required && {
                                '&::after': {
                                    content: '" *"',
                                }
                            })
                        }}
                    >
                        {label}
                    </Box>

                    {required && (
                        <input
                            type="text"
                            tabIndex={-1}
                            autoComplete="off"
                            style={{ opacity: 0, width: 0, height: 0, position: "absolute" }}
                            value={hiddenInputValue}
                            required
                            name={name}
                            onChange={() => { }}
                        />
                    )}
                    <SelectComponent
                        {...rest}
                        value={value}
                        ref={ref}
                        styles={{
                            ...reactSelectStyles,
                            control: (provided, state) => ({
                                ...reactSelectStyles.control(provided, state),
                                border: `1px solid ${isError
                                    ? theme.palette.error.main
                                    : state.isFocused
                                        ? theme.palette.info.main
                                        : theme.palette.grey[300]
                                    }`,
                                boxShadow: 'none',
                                backgroundColor: disabled ? '#f2f3f2' : '#fff',
                                '&:hover': {
                                    borderColor: isError
                                        ? theme.palette.error.main
                                        : state.isFocused
                                            ? theme.palette.info.main
                                            : theme.palette.grey[400], // Changed from text.primary to gray
                                },
                                '&:focus':{ borderWidth: '2px' }
                            }),
                            // Updated option styles for better contrast
                            option: (provided, state) => ({
                                ...provided,
                                fontSize: '0.875rem',
                                padding: '8px 10px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                backgroundColor: state.isSelected
                                    ? theme.palette.info.main // Lighter background for better contrast
                                    : state.isFocused
                                        ? theme.palette.action.hover
                                        : 'transparent',
                                color: state.isSelected
                                    ? theme.palette.info.contrastText
                                    : theme.palette.text.primary,
                                '&:hover': {
                                    backgroundColor: state.isSelected
                                        ? theme.palette.info.light
                                        : theme.palette.action.hover,
                                },
                                // Updated checkbox styles for better visibility
                                '&::before': {
                                    content: '""',
                                    display: 'inline-block',
                                    width: '16px',
                                    height: '16px',
                                    marginRight: '8px',
                                    border: `2px solid ${state.isSelected
                                        ? theme.palette.common.white // White border when selected
                                        : theme.palette.grey[500] // Darker gray when unselected
                                        }`,
                                    borderRadius: '3px',
                                    backgroundColor: state.isSelected
                                        ? theme.palette.common.white // White background when selected
                                        : 'transparent',
                                    backgroundImage: state.isSelected
                                        ? `url("data:image/svg+xml,%3csvg viewBox='0 0 16 16' fill='${encodeURIComponent(theme.palette.primary.main)}' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='m13.854 3.646-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 9.293l6.646-6.647a.5.5 0 0 1 .708.708z'/%3e%3c/svg%3e")`
                                        : 'none',
                                    backgroundRepeat: 'no-repeat',
                                    backgroundPosition: 'center',
                                    backgroundSize: '12px',
                                },
                                ...customStyles.option?.(provided, state),
                            }),
                        }}
                        isDisabled={disabled}
                        isMulti
                        placeholder={shouldShrink ? placeholder : ""}
                        classNamePrefix="md-react-select"
                        options={options}
                        onChange={onChange}
                        hideSelectedOptions={false}
                        closeMenuOnSelect={false}
                        onFocus={(e) => {
                            setIsFocused(true);
                            rest.onFocus?.(e);
                        }}
                        onBlur={(e) => {
                            setIsFocused(false);
                            setWasTouched(true); // Set touched on blur
                            rest.onBlur?.(e);
                        }}
                        onMenuOpen={() => {
                            setMenuIsOpen(true);
                            rest.onMenuOpen?.();
                        }}
                        onMenuClose={() => {
                            setMenuIsOpen(false);
                            rest.onMenuClose?.();
                        }}
                        loadOptions={async ? rest.loadOptions : undefined}
                        defaultOptions={async ? rest.defaultOptions : undefined}
                        cacheOptions={async ? rest.cacheOptions : undefined}
                        isLoading={rest.isLoading}
                    />

                    {/* Error text for multi-select */}
                    {isError && (
                        <Box
                            sx={{
                                color: theme.palette.error.main,
                                fontSize: '0.75rem',
                                marginTop: '3px',
                                marginLeft: '14px',
                                fontFamily: theme.typography.fontFamily,
                            }}
                        >
                            {helperText || (Array.isArray(value) ? 'Please select at least one option.' : 'This field is required.')}
                        </Box>
                    )}
                </Box>
            );
        }

        // Single-select: keep existing TextField overlay behavior
        return (
            <Box sx={{ position: 'relative' }}>
                <TextField
                    fullWidth={fullWidth}
                    label={label}
                    error={isError}
                    disabled={disabled}
                    required={required}
                    variant="outlined"
                    focused={isFocused || menuIsOpen}
                    InputLabelProps={{
                        shrink: shouldShrink,
                    }}
                    InputProps={{
                        inputComponent: HiddenInput,
                    }}
                    sx={{
                        '& .MuiFormControl-root': {
                            height: '44px',
                            minHeight: '44px',
                        },
                        '& .MuiOutlinedInput-root': {
                            padding: 0,
                            minHeight: '44px',
                            '& fieldset': {
                                borderColor: isError
                                    ? theme.palette.error.main
                                    : theme.palette.grey[300],
                            },
                            '&:hover fieldset': {
                                borderColor: isError
                                    ? theme.palette.error.main
                                    : theme.palette.grey[400], // Changed from text.primary to gray
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: isError
                                    ? theme.palette.error.main
                                    : theme.palette.primary.info,
                                borderWidth: '2px',
                            },
                        },
                        '& .MuiInputLabel-root': {
                            fontSize: '0.875rem',
                            '&.Mui-focused': {
                                color: isError
                                    ? theme.palette.error.main
                                    : theme.palette.primary.info,
                            },
                        },
                        '& .MuiOutlinedInput-input': {
                            padding: '0 !important',
                            margin: '0 !important',
                            minHeight: '44px !important',
                            height: '44px !important',
                            overflow: 'hidden !important',
                        },
                    }}
                    helperText={isError && required ? (Array.isArray(value) ? 'Please select at least one option.' : 'This field is required.') : ''}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        minHeight: '44px'
                    }}
                >
                    {required && (
                        <input
                            type="text"
                            tabIndex={-1}
                            autoComplete="off"
                            style={{ opacity: 0, width: 0, height: 0, position: "absolute", bottom: 0, right: '50%' }}
                            value={hiddenInputValue}
                            required
                            name={name}
                            onChange={() => { }}
                        />
                    )}
                    <SelectComponent
                        {...rest}
                        value={value}
                        ref={ref}
                        styles={reactSelectStyles}
                        isDisabled={disabled}
                        isMulti={false}
                        placeholder={shouldShrink ? placeholder : ""}
                        classNamePrefix="md-react-select"
                        options={options}
                        onChange={onChange}
                        hideSelectedOptions={false}
                        closeMenuOnSelect
                        onFocus={(e) => {
                            setIsFocused(true);
                            rest.onFocus?.(e);
                        }}
                        onBlur={(e) => {
                            setIsFocused(false);
                            rest.onBlur?.(e);
                        }}
                        onMenuOpen={() => {
                            setMenuIsOpen(true);
                            rest.onMenuOpen?.();
                        }}
                        onMenuClose={() => {
                            setMenuIsOpen(false);
                            rest.onMenuClose?.();
                        }}
                        loadOptions={async ? rest.loadOptions : undefined}
                        defaultOptions={async ? rest.defaultOptions : undefined}
                        cacheOptions={async ? rest.cacheOptions : undefined}
                        isLoading={rest.isLoading}
                    />
                </Box>
            </Box>
        );

    }
);

// Setting default values for the props of CustomReactSelect
CustomReactSelect.defaultProps = {
    error: false,
    success: false,
    disabled: false,
    fullWidth: true,
    label: "",
    required: false,
    async: false,
    customStyles: {},
    placeholder: "",
    menuPlacement: "auto",
    maxMenuHeight: 300,
    isMulti: false,
    options: []
};

// Typechecking props for the CustomReactSelect
CustomReactSelect.propTypes = {
    error: PropTypes.bool,
    success: PropTypes.bool,
    disabled: PropTypes.bool,
    fullWidth: PropTypes.bool,
    label: PropTypes.string,
    required: PropTypes.bool,
    async: PropTypes.bool,
    customStyles: PropTypes.object,
    value: PropTypes.any,
    placeholder: PropTypes.string,
    loadOptions: PropTypes.func,
    defaultOptions: PropTypes.oneOfType([PropTypes.bool, PropTypes.array]),
    cacheOptions: PropTypes.bool,
    isLoading: PropTypes.bool,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onMenuOpen: PropTypes.func,
    onMenuClose: PropTypes.func,
    name: PropTypes.string,
    isMulti: PropTypes.bool,
    options: PropTypes.array,
    onChange: PropTypes.func,
    creatable: PropTypes.bool
};

export default CustomReactSelect;